/* eslint-disable no-magic-numbers */
// magic numbers are used to make the layout
import { CoreContainer } from '@hub/core-container'
import { H2 } from '@hub/heading'
import { Stack } from '@hub/stack'
import { Text } from '@hub/text'
import React from 'react'
import { Cluster } from '@hub/cluster'
import { IncludeDirectLogo } from '@hub/icon/src/with-direct-logo'
import { Grid } from '@hub/grid'
import { LinkBox, LinkOverlay } from '@hub/link'

import { SGPageTemplateContentEntry } from '../../../types/page-templates'
import { AsMeasured } from '../as-measured'
import { CloudinaryImage } from '../cloudinary-image'
import { CategoryTile } from './category-tile'

interface CategorySpotlightProps {
  title?: string | null
  text?: string | null
  items: SGPageTemplateContentEntry[]
  analyticsSlot?: string
}

export const CategorySpotlight: React.FC<CategorySpotlightProps> = ({
  title,
  text,
  analyticsSlot,
  items: _items,
}) => {
  const items = [..._items] // take a copy to avoid mutating input value
  const item1 = items.shift()
  const item2 = items.shift()
  const item3 = items.shift()

  return (
    <CoreContainer>
      <Stack gap="spacing-sm">
        <Stack gap="spacing-none">
          <H2 align="center">{title && <IncludeDirectLogo title={title} />}</H2>
          <Text isBodyText align="center" color="quartz">
            {text}
          </Text>
        </Stack>
        <Stack gap={['spacing-sm', 'spacing-md']}>
          <Cluster
            wrap="wrap"
            gap={['spacing-sm', 'spacing-md']}
            align="center"
          >
            {item1 && (
              <CategoryTile
                as={props => (
                  <AsMeasured
                    {...props}
                    metadata={item1.analyticsMetadata}
                    slot={analyticsSlot}
                  />
                )}
                sx={{
                  order: [null, 2],
                  minWidth: ['size-19', '36%'],
                  flexBasis: ['size-full', 'size-16', '36%'],
                  flexGrow: [1, 'initial'],
                  flexShrink: 1,
                }}
                {...item1}
              />
            )}
            {item2 && (
              <CategoryTile
                as={props => (
                  <AsMeasured
                    {...props}
                    metadata={item2.analyticsMetadata}
                    slot={analyticsSlot}
                  />
                )}
                sx={{
                  order: [null, 1],
                  minWidth: ['size-13', 'size-10', 'size-15'],
                  flexBasis: ['size-13', 'size-10', 'size-15'],
                  flexGrow: 1,
                  flexShrink: 1,
                }}
                {...item2}
              />
            )}
            {item3 && (
              <CategoryTile
                as={props => (
                  <AsMeasured
                    {...props}
                    metadata={item3.analyticsMetadata}
                    slot={analyticsSlot}
                  />
                )}
                sx={{
                  order: [null, 3],
                  minWidth: ['size-13', 'size-10', 'size-15'],
                  flexBasis: ['size-13', 'size-10', 'size-15'],
                  flexGrow: 1,
                  flexShrink: 1,
                }}
                {...item3}
              />
            )}
          </Cluster>
          <Grid
            gap={['spacing-sm', 'spacing-md']}
            templateColumns={[
              'repeat(2, 1fr)',
              'repeat(3, 1fr)',
              null,
              'repeat(6 ,1fr)',
            ]}
            maxWidth={['size-lg', 'size-lg', 'size-lg', 'size-md']}
          >
            {items.map(item => (
              <LinkBox key={item.title}>
                <Stack
                  direction={['row', 'row', 'row', 'column']}
                  shouldWrapChildren={false}
                  gap={[
                    'spacing-sm',
                    'spacing-sm',
                    'spacing-sm',
                    'spacing-none',
                  ]}
                  sx={{
                    alignContent: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'smoke',
                    height: 'size-full',
                    padding: ['spacing-sm', 'spacing-sm', 'spacing-sm', null],
                  }}
                  as={props => (
                    <AsMeasured
                      {...props}
                      metadata={item.analyticsMetadata}
                      slot={analyticsSlot}
                    />
                  )}
                >
                  <CloudinaryImage
                    ratio="1"
                    imageSetOrImage={item.image}
                    minWidth="size-xs"
                    sx={{
                      marginX: [null, null, null, 'spacing-lg'],
                      marginTop: [null, null, null, 'spacing-xs'],
                      marginBottom: [null, null, null, 'spacing-sm'],
                      flexShrink: [0, 0, 0, 1],
                      flexGrow: [0, 0, 0, 1],
                      flexBasis: ['size-8', 'size-8', 'size-sm', 'auto'],
                    }}
                  />
                  <Text
                    as="span"
                    fontSize={['font-md', 'font-xl']}
                    tone="strong"
                    align={['left', 'left', 'left', 'center']}
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: [
                        'flex-start',
                        'flex-start',
                        'flex-start',
                        'center',
                      ],
                    }}
                  >
                    <LinkOverlay href={item.callToAction.url}>
                      {item.title}
                    </LinkOverlay>
                  </Text>
                </Stack>
              </LinkBox>
            ))}
          </Grid>
        </Stack>
      </Stack>
    </CoreContainer>
  )
}
